import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppIcon from '../../../components/AppIcon';
import UserProfile from '../../UserProfile';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { formatDate } from '../../../utils/dateFormatter';
import { DatePicker as CustomDatePicker, formatDateForInput } from '../../../components/dateTimePickers';
import { setImageUrl, setSelectedUser, setUserFaceImage } from '../../../store/redux/actions/userActionCreator';
import { getUser, getUserInfoById, FetchUsers, updateProfile, getRoles, getUserManagers } from '../../../api/General/account';
import { getUserCustomFieldValues, setUserCustomFieldValues, UserCustomFieldValueView } from '../../../api/General/customFields';
import { EditUserCurrencyAndRule, GetCompanyRates } from '../../../api/General/currency';
import { EditUserBank, EditUserBankRule, GetBanks } from '../../../api/General/bank';
import { getTimeZones, getCountries } from '../../../api/General/Country';
import { getBranches, getDepartments } from '../../../api/General/Company';
import {
  Currency, UserCurrency, Bank, UserBank, Gender, MaritalStatus, ProfileInput, UserView, TimeZoneInfoView, Roles as SystemRoles
} from '../../../models/general';
import { probationMaxDaysFor, probationLegalReferenceFor } from '../../../models/payroll';
import { LawReference } from '../../Payroll/ComplianceCenter/components/LawReference';
import { Roles as RoleGroups } from '../../../components/roles';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import { useCompanyCapabilities } from '../../../hooks/useCompanyCapabilities';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { AlertTriangle } from 'lucide-react';
import EducationSection from './sections/EducationSection';
import WorkExperienceSection from './sections/WorkExperienceSection';
import TermsAcceptanceSection from './sections/TermsAcceptanceSection';
import { LoadingState } from '../../../components/AsyncStates';
import { startOffboarding, terminateDirect, reverseTermination, getOffboardingEmployeeSelections } from '../../../api/Employee/offboardingProcesses';
import { GetEosContext } from '../../../api/Payroll/payroll';
import { getTemplates } from '../../../api/Employee/lifecycleTemplates';
import { getExitReasons } from '../../../api/Employee/lifecycleSettings';
import type { LifecycleTemplateView, ExitReasonView, EmployeeSelectionView } from '../../../models/employee';
import { getGosiEmployeeRecord, upsertGosiEmployeeRecord } from '../../../api/Payroll/gosi';



const monthsArray = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const FieldRow =({ label, value, editMode, editContent }: { label: string; value: React.ReactNode; editMode: boolean; editContent?: React.ReactNode }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-border/40 last:border-b-0">
    <dt className="w-40 shrink-0 text-xs uppercase tracking-wide text-muted-foreground font-medium pt-0.5">{label}</dt>
    <dd className="text-sm text-foreground min-w-0 flex-1">{editMode && editContent ? editContent : (value || <span className="text-muted-foreground/60">--</span>)}</dd>
  </div>
);

interface Props { role?: any; userId: string; imageUrl?: string; userFaceImage?: any; userCurrencyRule?: UserCurrency; company?: any; setImageUrl: (u: string) => void; setUserFaceImage: (i: any) => void; setSelectedUser: (u: UserView | undefined) => void; }

const Personal = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const userId = props.userId;
  const complianceCountry = useComplianceCountry();

  const subjectUserId = id ?? userId;

  const isViewingAnotherUser = !!id && id !== userId;
  const hideEducationWorkDocs = RoleGroups.ADMIN.includes(props.role) && isViewingAnotherUser;
  const isHr = RoleGroups.HR.includes(props.role);

  const [input, setInput] = useState<ProfileInput>({
    image: null, employeeId: '', companyId: 0, countryId: 0, title: '', firstName: '', lastName: '', fullName: '', linkedInUrl: '', birthDate: new Date(), hireDate: new Date(), probationEndDate: undefined, phoneNumber: '', role: '', managerIds: [], timeZone: '', fiscalYearStartingMonth: 1, branchId: 0, departmentId: 0, userName: '', email: '', gender: Gender.Male, maritalStatus: undefined, ibanNumber: '', employeeUniqueId: '', vehicleId: '', employmentType: undefined, molListNumber: '', fileNumber: '', uidNumber: '', visaDesignation: '', bankAccountNumber: '', address: '', nationalityId: undefined, emirate: undefined, passportNumber: undefined, passportExpiryDate: undefined, emiratesIdNumber: undefined, emiratesIdExpiryDate: undefined, labourCardNumber: undefined, labourCardExpiryDate: undefined, visaIdNumber: undefined, visaExpiryDate: undefined, personalEmail: '', iqamaNumber: undefined, iqamaExpiryDate: undefined, saudiNationalIdNumber: undefined, qiwaContractNumber: undefined, muqeemIdNumber: undefined
  });
  const [user, setUser] = useState<UserView>();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [timeZones, setTimezones] = useState<TimeZoneInfoView[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<UserView[]>([]);

  const [userCurrencyRule, setUserCurrencyRule] = useState<UserCurrency>(UserCurrency.InheritedFromCompany);
  const [userCurrency, setUserCurrency] = useState<Currency | undefined>();
  const [newCurrency, setNewCurrency] = useState<Currency | undefined>();
  const [newCurrencyRule, setNewCurrencyRule] = useState<UserCurrency | null>(UserCurrency.InheritedFromCompany);
  const [currencyChanged, setCurrencyChanged] = useState(false);
  const [currencyRuleChanged, setCurrencyRuleChanged] = useState(false);
  const [userBankRule, setUserBankRule] = useState<UserBank>(UserBank.InheritedFromCompany);
  const [userBank, setUserBank] = useState<Bank | undefined>();
  const [newBank, setNewBank] = useState<Bank | undefined>();
  const [newBankRule, setNewBankRule] = useState<UserBank | null>(UserBank.InheritedFromCompany);
  const [bankChanged, setBankChanged] = useState(false);
  const [bankRuleChanged, setBankRuleChanged] = useState(false);

  const [editMode, setEditMode] = useState(false);
  // Only the HR record is capability-gated; account settings such as password and language are not.
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthorEmployees = capabilitiesLoaded && can('canAuthorEmployees');
  // HR-defined custom fields for this employee (join of company definitions + the employee's values).
  const [customValues, setCustomValues] = useState<UserCustomFieldValueView[]>([]);
  const [customValuesChanged, setCustomValuesChanged] = useState(false);
  const [profileInfoChanged, setProfileInfoChanged] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailHelper, setEmailHelper] = useState('');
  const [ibanError, setIbanError] = useState(false);
  const [ibanHelper, setIbanHelper] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [openTerminate, setOpenTerminate] = useState(false);
  const [offboardingTemplates, setOffboardingTemplates] = useState<LifecycleTemplateView[]>([]);
  const [offboardingExitReasons, setOffboardingExitReasons] = useState<ExitReasonView[]>([]);
  const [offboardingManagers, setOffboardingManagers] = useState<EmployeeSelectionView[]>([]);
  const [offboardingFormData, setOffboardingFormData] = useState({
    templateId: '',
    lastWorkingDay: '',
    exitReasonId: '',
    managerId: '',
  });
  const [offboardingSubmitting, setOffboardingSubmitting] = useState(false);
  const [offboardingError, setOffboardingError] = useState<string | null>(null);
  // Reverse-termination (A4) dialog state.
  const [openReverse, setOpenReverse] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseAllowPaid, setReverseAllowPaid] = useState(false);
  const [reverseHasPaidSettlement, setReverseHasPaidSettlement] = useState(false);
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);

  const maritalStatusLabels: Record<string, string> = {
    Single: t('Single'),
    Engaged: t('Engaged'),
    Married: t('Married'),
    Divorced: t('Divorced'),
    Widowed: t('Widowed'),
  };
  const employmentTypeOptions = ['FullTime', 'PartTime', 'Contract', 'Executive'] as const;
  // UAE emirates — values match the GpssaService check ("Abu Dhabi" → ADPF; others → GPSSA).
  const emirateOptions = [
    'Abu Dhabi',
    'Dubai',
    'Sharjah',
    'Ajman',
    'Umm Al Quwain',
    'Ras Al Khaimah',
    'Fujairah',
  ] as const;
  const isUaeNationality = (nid?: number) => {
    const c = countries.find(x => x.id === nid);
    return (c?.iso ?? '').toUpperCase() === 'AE';
  };
  const employmentTypeLabels: Record<string, string> = {
    FullTime: t('Full Time'),
    PartTime: t('Part Time'),
    Contract: t('Contract'),
    Executive: t('Executive'),
  };

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const ms = toast.type === 'success' ? 6000 : 7000;
    const timer = setTimeout(() => setToast(null), ms);
    return () => clearTimeout(timer);
  }, [toast]);

  const validateEmail = (value: string) => { if (!value) { setEmailError(false); setEmailHelper(''); return; } const valid = /.+@.+\..+/.test(value); setEmailError(!valid); setEmailHelper(!valid ? t('Invalid email') : ''); };
  const validateIban = (v: string) => { if (!v) { setIbanError(false); setIbanHelper(''); return; } if (v.length < 8) { setIbanError(true); setIbanHelper(t('Too short')); } else { setIbanError(false); setIbanHelper(''); } };

  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const userRes = await getUser(id ?? userId);
      if (userRes.errors?.length > 0) { setFetchError(getApiErrorMessage(userRes, t)); return; }
      const u = userRes.payload; setUser(u); props.setSelectedUser(u);
      setInput(i => ({ ...i, employeeId: u.employeeId, companyId: u.company?.id, countryId: u.country?.id, title: u.title, firstName: u.firstName, lastName: u.lastName, fullName: u.fullName, linkedInUrl: u.linkedInUrl, birthDate: u.birthDate as any, hireDate: u.hireDate as any, probationEndDate: u.probationEndDate as any ?? undefined, phoneNumber: u.phoneNumber, role: u.role, managerIds: u.managerIds ?? [], timeZone: u.timeZone, fiscalYearStartingMonth: u.fiscalYearStartingMonth ?? 1, branchId: u.branchId, departmentId: u.departmentId, userName: u.userName, email: u.email, gender: u.gender ?? Gender.Male, maritalStatus: u.maritalStatus ?? undefined, ibanNumber: u.ibanNumber, employeeUniqueId: u.employeeUniqueId, vehicleId: u.vehicleId, employmentType: u.employmentType ?? undefined, molListNumber: u.molListNumber ?? '', fileNumber: u.fileNumber ?? '', uidNumber: u.uidNumber ?? '', visaDesignation: u.visaDesignation ?? '', bankAccountNumber: u.bankAccountNumber ?? '', address: u.address ?? '', nationalityId: u.nationalityId, emirate: u.emirate ?? undefined, passportNumber: u.passportNumber, passportExpiryDate: u.passportExpiryDate, emiratesIdNumber: u.emiratesIdNumber, emiratesIdExpiryDate: u.emiratesIdExpiryDate, labourCardNumber: u.labourCardNumber, labourCardExpiryDate: u.labourCardExpiryDate, visaIdNumber: u.visaIdNumber, visaExpiryDate: u.visaExpiryDate, personalEmail: u.personalEmail ?? '', iqamaNumber: u.iqamaNumber, iqamaExpiryDate: u.iqamaExpiryDate, saudiNationalIdNumber: u.saudiNationalIdNumber, qiwaContractNumber: u.qiwaContractNumber, muqeemIdNumber: u.muqeemIdNumber, visaCategory: u.visaCategory ?? undefined }));
      const allUsersRes = await FetchUsers('Company', props.company?.id, false); if (allUsersRes.errors?.length === 0) setAllUsers(allUsersRes.payload);
      const companyRates = await GetCompanyRates(); if (companyRates.errors?.length === 0 && companyRates.payload) { const companyCurrencies: Currency[] = companyRates.payload.map((c: any) => ({ id: c.foreignCurrency.id, country: c.foreignCurrency.country, countryId: c.foreignCurrency.countryId, currencyCode: c.foreignCurrency.currencyCode, currencySymbol: c.foreignCurrency.currencySymbol, name: c.foreignCurrency.name, createdOn: new Date(), isArchived: false })); setCurrencies(companyCurrencies); }
      const banksRes = await GetBanks(); if (banksRes.errors?.length === 0) setBanks(banksRes.payload);
      const userInfo = await getUserInfoById(id ?? userId);
      if (userInfo.errors?.length === 0 && userInfo.payload?.userRules) { setUserCurrencyRule(userInfo.payload.userRules.userCurrency); setUserBankRule(userInfo.payload.userRules.userBank); setUserCurrency(userInfo.payload.userRules.currency); setUserBank(userInfo.payload.userRules.bank); }
      const tz = await getTimeZones(); if (tz.errors?.length === 0) setTimezones(tz.payload);
      const rs = await getRoles(); if (rs.errors?.length === 0) setRoles(rs.payload);
      if (u.company?.id) { const countryRes = await getCountries(); setCountries(countryRes.payload ?? []); const br = await getBranches(u.company.id); setBranches(br.payload ?? []); const deps = await getDepartments(u.company.id); setDepartments(deps.payload ?? []); }
      // Custom-field values are gated to self / the employee's manager / Hr-Admin. Isolate this
      // fetch so a viewer without read access (a 403) — or a transient failure — just hides the
      // custom-fields card rather than tearing down the whole profile via the shared catch below.
      try {
        const cf = await getUserCustomFieldValues(id ?? userId);
        if (!cf.errors?.length) setCustomValues(cf.payload ?? []);
      } catch { /* no custom-field read access or transient error — hide the card, keep the page */ }
      setCustomValuesChanged(false);
    } catch {
      setFetchError(t('Failed to load profile data'));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData();   }, [id]);

  // Deep-link support: compliance/WPS screens link here with a hash (#iban,
  // #ksa-identity, #gosi) to point HR at the exact field that needs fixing. Scroll the
  // target into view once the profile has loaded (sections only exist in the DOM after
  // the loading state clears).
  useEffect(() => {
    if (loading || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, location.hash]);

  useEffect(() => {
    if (!openTerminate) return;
    setOffboardingFormData({ templateId: '', lastWorkingDay: '', exitReasonId: '', managerId: '' });
    setOffboardingError(null);

    getTemplates(1).then(r => {
      if (!r.errors?.length) setOffboardingTemplates(r.payload || []);
    });
    getExitReasons().then(r => {
      if (!r.errors?.length) setOffboardingExitReasons((r.payload || []).filter((er: ExitReasonView) => er.isActive));
    });
    getUserManagers(subjectUserId).then(r => {
      if (!r.errors?.length && r.payload?.length) {
        setOffboardingManagers(r.payload.map((m) => ({
          id: m.id,
          fullName: m.fullName || `${m.firstName} ${m.lastName}`,
        } as EmployeeSelectionView)));
      } else {
        getOffboardingEmployeeSelections().then(sel => {
          if (!sel.errors?.length) setOffboardingManagers(sel.payload || []);
        });
      }
    });
  }, [openTerminate, subjectUserId]);

  const EditBasicInfo = async () => {
    if (!canAuthorEmployees) return;
    if (!editMode) { setEditMode(true); return; }
    // GOSI pre-flight: when employee has a Saudi National ID, the subscription date is required.
    if (complianceCountry === 'KSA' && input.saudiNationalIdNumber && gosiDirty && !gosiSubscriptionDate) {
      setGosiSubscriptionError(t('profile.gosi.errors.subscriptionRequired'));
      setToast({ message: t('profile.gosi.errors.subscriptionRequired'), type: 'error' });
      return;
    }
    try {
      if (currencyChanged || currencyRuleChanged) {
        await EditUserCurrencyAndRule(id ?? userId, newCurrencyRule as UserCurrency, newCurrency?.id as number);
      }
      if (bankChanged) {
        await EditUserBank(id ?? userId, newBank?.id as number);
      }
      if (bankRuleChanged) {
        await EditUserBankRule(id ?? userId, newBankRule as UserBank);
      }
      await updateProfile(input, user?.id);

      // Custom-field values save through their own (Hr-gated) endpoint, not the multipart profile.
      if (customValuesChanged && isHr) {
        await setUserCustomFieldValues(subjectUserId, customValues.map(c => ({ definitionId: c.definitionId, value: c.value })));
      }

      if (complianceCountry === 'KSA' && input.saudiNationalIdNumber && gosiDirty && gosiSubscriptionDate) {
        const gosiResult = await upsertGosiEmployeeRecord(subjectUserId, {
          userId: subjectUserId,
          gosiSubscriptionDate,
          isLegacyScheme: gosiIsLegacy,
        });
        if (gosiResult.errors.length > 0) {
          const desc = gosiResult.errors[0]?.description || t("We couldn't save your profile changes. Please review the fields and try again.");
          setToast({ message: desc, type: 'error' });
          return;
        }
        setGosiDirty(false);
      }

      setEditMode(false);
      fetchData();
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        t,
        t("We couldn't save your profile changes. Please review the fields and try again.")
      );
      setToast({ message: msg, type: 'error' });
    }
  };

  const handleOpenTerminate = () => {
    if (!canAuthorEmployees) return;
    setOpenTerminate(o => !o);
  };

  const handleStartOffboarding = async () => {
    setOffboardingSubmitting(true);
    setOffboardingError(null);
    try {
      const result = await startOffboarding({
        userId: subjectUserId,
        templateId: offboardingFormData.templateId ? parseInt(offboardingFormData.templateId, 10) : undefined,
        lastWorkingDay: offboardingFormData.lastWorkingDay,
        exitReasonId: parseInt(offboardingFormData.exitReasonId, 10),
        managerId: offboardingFormData.managerId,
      });
      if (result.errors && result.errors.length > 0) {
        setOffboardingError(getApiErrorMessage(result, t, t('Failed to start offboarding')));
      } else {
        setToast({ message: t('Offboarding process started successfully.'), type: 'success' });
        setOpenTerminate(false);
      }
    } catch (err) {
      setOffboardingError(getApiErrorMessage(err, t, t('Failed to start offboarding')));
    } finally {
      setOffboardingSubmitting(false);
    }
  };

  // Fast-terminate: no template selected → terminate immediately (no offboarding process) and calc EOS now.
  const handleFastTerminate = async () => {
    setOffboardingSubmitting(true);
    setOffboardingError(null);
    try {
      const result = await terminateDirect({
        userId: subjectUserId,
        exitReasonId: offboardingFormData.exitReasonId ? parseInt(offboardingFormData.exitReasonId, 10) : undefined,
        lastWorkingDay: offboardingFormData.lastWorkingDay || undefined,
      });
      if (result.errors && result.errors.length > 0) {
        setOffboardingError(getApiErrorMessage(result, t, t('Failed to terminate employee')));
      } else {
        const p = result.payload;
        const message = p?.eosCalculated && p.eosAmount != null
          ? t('Employee terminated. End-of-service calculated: {{amount}}', { amount: Number(p.eosAmount).toLocaleString(undefined, { maximumFractionDigits: 2 }), nsSeparator: false })
          : (p?.note || t('Employee terminated.'));
        setToast({ message, type: 'success' });
        setOpenTerminate(false);
        fetchData();
      }
    } catch (err) {
      setOffboardingError(getApiErrorMessage(err, t, t('Failed to terminate employee')));
    } finally {
      setOffboardingSubmitting(false);
    }
  };

  // Reverse a completed termination/resignation (A4). Opens the confirm dialog and, as a prerequisite
  // read, checks whether a Paid settlement exists so the override affordance can be surfaced up front.
  const handleOpenReverse = () => {
    if (!canAuthorEmployees) return;
    setReverseReason('');
    setReverseAllowPaid(false);
    setReverseHasPaidSettlement(false);
    setReverseError(null);
    setOpenReverse(true);
    GetEosContext(subjectUserId)
      .then(r => {
        if (!r.errors?.length && r.payload?.settlementStatus === 'Paid') setReverseHasPaidSettlement(true);
      })
      .catch(() => { /* backend still enforces the paid-settlement guard on submit */ });
  };

  const handleReverseTermination = async () => {
    if (!reverseReason.trim()) {
      setReverseError(t('A reason is required to reverse a termination.'));
      return;
    }
    setReverseSubmitting(true);
    setReverseError(null);
    try {
      const result = await reverseTermination({
        userId: subjectUserId,
        reason: reverseReason.trim(),
        allowPaidSettlementReversal: reverseAllowPaid,
      });
      if (result.errors && result.errors.length > 0) {
        const msg = getApiErrorMessage(result, t, t('Failed to reverse termination.'));
        // If the block was the paid-settlement guard, reveal the override affordance.
        if (/paid/i.test(msg)) setReverseHasPaidSettlement(true);
        setReverseError(msg);
      } else {
        setToast({ message: t('Termination reversed. The employee is now active.'), type: 'success' });
        setOpenReverse(false);
        fetchData();
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t, t('Failed to reverse termination.'));
      if (/paid/i.test(msg)) setReverseHasPaidSettlement(true);
      setReverseError(msg);
    } finally {
      setReverseSubmitting(false);
    }
  };

  const updateCustomValue = (definitionId: number, value: string) => {
    setCustomValues(prev => prev.map(c => c.definitionId === definitionId ? { ...c, value } : c));
    setCustomValuesChanged(true);
  };
  const customFieldLabel = (c: UserCustomFieldValueView) => (i18n.language?.startsWith('ar') && c.labelAr ? c.labelAr : c.label);

  const CurrencyRuleChanged = (val: any) => { setNewCurrencyRule(val); setCurrencyRuleChanged(true); };
  const CurrencyChanged = (val: any) => { setNewCurrency(val); setCurrencyChanged(true); };
  const BankRuleChanged = (val: any) => { setNewBankRule(val); setBankRuleChanged(true); };
  const BankChanged = (val: any) => { setNewBank(val); setBankChanged(true); };

  const managerOptions = React.useMemo(
    () => allUsers
      .filter(u => String(u.id) !== String(subjectUserId))
      .map(u => ({ id: u.id, label: u.fullName })),
    [allUsers, subjectUserId]
  );

  const isSafeUrl = (url: string) => {
    try { const parsed = new URL(url); return ['http:', 'https:'].includes(parsed.protocol); }
    catch { return false; }
  };

  // Self-edit restrictions: employees can only edit certain fields on their own profile.
  //
  // CO-248: this list MIRRORS the server's authority (AccountService.SelfEditableProfileFields) and
  // is only ever used to decide what to render as editable — the server enforces it independently
  // and ignores anything outside it. Keep the two in sync; the server's copy wins.
  const SELF_EDITABLE_FIELDS = [
    'phoneNumber', 'address', 'personalEmail', 'maritalStatus',
    'birthDate', 'gender', 'linkedInUrl', 'nationalityId', 'emirate'
  ];
  const isSelfEdit = !isViewingAnotherUser;
  const canEditField = (field: string) => {
    // HR is the company-wide HR authority: full edit, on anyone (server: editingUserDataAllowed).
    if (isHr) return true;
    // CO-248 — Admin used to be lumped in with HR here, which rendered every field editable on an
    // Admin's OWN profile and, for another employee, a whole editable form the server answers 401
    // to. Per the Role Model an Admin is a company admin AND a regular employee but NOT an HR role:
    // no HR-owned data for other employees, and on their own record the ordinary employee
    // self-service whitelist. This now matches what the server has always enforced.
    if (!isSelfEdit) return false;
    return SELF_EDITABLE_FIELDS.includes(field);
  };
  // CO-248: HR may edit anyone; everyone else (Admin included) may only enter edit mode on
  // their OWN profile. An Admin used to get an Edit button on another employee's profile that
  // opened a fully editable form the server answers 401 to. The capability gate is ANDed on top:
  // in a company where employee records are authored elsewhere, nobody edits here — not even HR.
  const canEnterEditMode = (isHr || isSelfEdit) && canAuthorEmployees;

  const [personalEmailError, setPersonalEmailError] = useState('');
  const validatePersonalEmail = (value: string) => {
    if (!value) { setPersonalEmailError(''); return; }
    const valid = /.+@.+\..+/.test(value);
    setPersonalEmailError(!valid ? t('Invalid email') : '');
  };

  const [iqamaError, setIqamaError] = useState('');
  const [saudiNationalIdError, setSaudiNationalIdError] = useState('');

  const validateIqama = (value: string) => {
    if (!value) { setIqamaError(''); return; }
    setIqamaError(/^2\d{9}$/.test(value) ? '' : t('iqamaFormatInvalid'));
  };

  const validateSaudiNationalId = (value: string) => {
    if (!value) { setSaudiNationalIdError(''); return; }
    setSaudiNationalIdError(/^1\d{9}$/.test(value) ? '' : t('saudiNationalIdFormatInvalid'));
  };

  // GOSI sub-block state (KSA only). Used when the employee has a Saudi National ID.
  const [gosiSubscriptionDate, setGosiSubscriptionDate] = useState<string>('');
  const [gosiIsLegacy, setGosiIsLegacy] = useState<boolean>(false);
  const [gosiSubscriptionError, setGosiSubscriptionError] = useState<string | null>(null);
  const [gosiDirty, setGosiDirty] = useState<boolean>(false);

  useEffect(() => {
    if (complianceCountry !== 'KSA') return;
    if (!input.saudiNationalIdNumber) {
      setGosiSubscriptionDate('');
      setGosiIsLegacy(false);
      setGosiSubscriptionError(null);
      setGosiDirty(false);
      return;
    }
    (async () => {
      try {
        const result = await getGosiEmployeeRecord(subjectUserId);
        if (result.errors.length === 0 && result.payload) {
          setGosiSubscriptionDate(result.payload.gosiSubscriptionDate
            ? result.payload.gosiSubscriptionDate.slice(0, 10)
            : '');
          setGosiIsLegacy(result.payload.isLegacyScheme);
          setGosiSubscriptionError(null);
          setGosiDirty(false);
        }
      } catch {
        /* not found is fine — first-time set */
      }
    })();
  }, [complianceCountry, input.saudiNationalIdNumber, subjectUserId]);

  return (
    <div className="flex flex-col">
      <UserProfile>
        <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
          <ManagedElsewhereNotice capability="canAuthorEmployees" />
          {/* Action buttons - same logic, slightly tighter */}
          <div className="sticky top-0 z-10 pb-2 bg-card/80 backdrop-blur-sm">
            <div className="flex flex-row justify-end gap-1.5">
              {!editMode && canEnterEditMode && (
                <Button size="sm" variant="outline" iconName="Pencil" onClick={() => { if (!canEnterEditMode) return; setEditMode(true); }}>{t('Edit')}</Button>
              )}
              {editMode && canEnterEditMode && (
                <>
                  <Button size="sm" iconName="CheckCircle2" onClick={EditBasicInfo}>{t('Save')}</Button>
                  <Button size="sm" variant="outline" onClick={() => { fetchData(); setEditMode(false); }}>{t('Cancel')}</Button>
                  {(profileInfoChanged || currencyRuleChanged || currencyChanged || bankChanged || bankRuleChanged) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                      {t('Unsaved changes')}
                    </span>
                  )}
                </>
              )}
              {/* Status-driven termination actions — Hr only, and never on one's own profile. */}
              {canAuthorEmployees && RoleGroups.HR.includes(props.role) && isViewingAnotherUser && (() => {
                const status = user?.employmentStatus;
                const chipBase = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border';
                if (status === 'Offboarding') {
                  // TODO(slice-4): surface View/Cancel offboarding actions here once the active process id is on the profile payload.
                  return (
                    <span className={`${chipBase} bg-warning/10 text-warning border-warning/20`}>
                      <AppIcon name="Clock" size={12} />
                      {t('Offboarding')}{user?.lastWorkingDay ? ` · ${t('last day')} ${formatDate(user.lastWorkingDay)}` : ''}
                    </span>
                  );
                }
                if (status === 'Terminated' || status === 'Resigned') {
                  const resigned = status === 'Resigned';
                  return (
                    <div className="inline-flex items-center gap-2">
                      <span className={`${chipBase} ${resigned ? 'bg-muted text-muted-foreground border-border' : 'bg-destructive/10 text-destructive border-destructive/20'}`}>
                        <AppIcon name={resigned ? 'LogOut' : 'UserX'} size={12} />
                        {t(status)}{user?.terminationDate ? ` · ${formatDate(user.terminationDate)}` : ''}
                      </span>
                      <Button size="sm" variant="outline" iconName="RotateCcw" onClick={handleOpenReverse}>{t('Reverse termination')}</Button>
                    </div>
                  );
                }
                return (
                  <Button size="sm" variant="destructive" onClick={handleOpenTerminate}>{t('Terminate Employee')}</Button>
                );
              })()}
            </div>
          </div>

          {/* Loading indicator */}
          {loading && (
            <LoadingState title={t('Loading...')} size="md" />
          )}

          {/* Error state */}
          {fetchError && !loading && (
            <div className="text-center py-8 text-destructive">{fetchError}</div>
          )}

          {/* Quick Glance Strip - view mode only */}
          {!loading && !fetchError && !editMode && (() => {
            const branchName = branches.find(b => b.id === input.branchId)?.name;
            const deptName = departments.find(d => d.id === input.departmentId)?.name;
            const countryName = countries.find(c => c.id === input.countryId)?.name;
            const managerNames = (input.managerIds ?? []).map(mid => allUsers.find(u => u.id === mid)?.fullName).filter(Boolean).join(', ');
            const chip = 'inline-flex items-center gap-1.5 bg-muted border border-border/40 rounded-full px-3 py-1 text-xs text-muted-foreground';
            return (
              <div className="flex flex-wrap gap-2">
                {input.hireDate && (
                  <span className={chip}><AppIcon name="Calendar" size={12} />{t('Hired')}: {new Date(input.hireDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                )}
                {branchName && <span className={chip}><AppIcon name="Building2" size={12} />{branchName}</span>}
                {input.employmentType && <span className={chip}><AppIcon name="Briefcase" size={12} />{employmentTypeLabels[input.employmentType] ?? input.employmentType}</span>}
                {deptName && <span className={chip}><AppIcon name="Users" size={12} />{deptName}</span>}
                {countryName && <span className={chip}><AppIcon name="Globe" size={12} />{countryName}</span>}
                {managerNames && <span className={chip}><AppIcon name="UserCheck" size={12} />{managerNames}</span>}
              </div>
            );
          })()}

          {/* Main two-column grid */}
          {!loading && !fetchError && <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* LEFT: Personal Details + Contact */}
            <Card variant="elevated">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <AppIcon name="User" size={16} className="text-primary" />
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('PROFILE & PERSONAL')}</h3>
                </div>
                <dl>
                  <FieldRow
                    editMode={editMode}
                    label={t('First Name')}
                    value={input.firstName}
                    editContent={<Input value={input.firstName} onChange={e => { setInput({ ...input, firstName: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('firstName')} />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Last Name')}
                    value={input.lastName}
                    editContent={<Input value={input.lastName} onChange={e => { setInput({ ...input, lastName: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('lastName')} />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Birth Date')}
                    value={input.birthDate ? new Date(input.birthDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null}
                    editContent={canEditField('birthDate') ? <CustomDatePicker label="" ariaLabel={t('Birth Date')} value={input.birthDate ?? null} onChange={(d: any) => { setInput({ ...input, birthDate: d ?? undefined }); setProfileInfoChanged(true); }} /> : <Input value={input.birthDate ? new Date(input.birthDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Gender')}
                    value={input.gender ? t(input.gender) : null}
                    editContent={canEditField('gender') ? <Select options={Object.values(Gender).map(g => ({ label: t(g), value: g }))} value={input.gender ?? Gender.Male} onChange={(v: any) => { setInput({ ...input, gender: v ?? Gender.Male }); setProfileInfoChanged(true); }} /> : <Input value={input.gender ? t(input.gender) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Marital Status')}
                    value={input.maritalStatus ? (maritalStatusLabels[input.maritalStatus] ?? input.maritalStatus) : null}
                    editContent={canEditField('maritalStatus') ? <Select options={Object.values(MaritalStatus).map(s => ({ label: maritalStatusLabels[s] ?? s, value: s }))} value={input.maritalStatus} onChange={(v: any) => { setInput({ ...input, maritalStatus: v ?? undefined }); setProfileInfoChanged(true); }} /> : <Input value={input.maritalStatus ? (maritalStatusLabels[input.maritalStatus] ?? input.maritalStatus) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('nationality')}
                    value={user?.nationality?.name || countries.find(c => c.id === user?.nationalityId)?.name}
                    editContent={canEditField('nationalityId') ? <Select options={countries.map(c => ({ label: c.name, value: c.id }))} value={input.nationalityId} onChange={(v: any) => {
                      const newId = v || undefined;
                      const stillUae = isUaeNationality(newId);
                      setInput({ ...input, nationalityId: newId, emirate: stillUae ? input.emirate : undefined });
                      setProfileInfoChanged(true);
                    }} searchable /> : <Input value={user?.nationality?.name || countries.find(c => c.id === user?.nationalityId)?.name || ''} disabled />}
                  />
                  {(isUaeNationality(input.nationalityId) || (!editMode && input.emirate)) && (
                    <FieldRow
                      editMode={editMode}
                      label={t('emirate', 'Emirate')}
                      value={input.emirate}
                      editContent={canEditField('emirate') ? <Select options={emirateOptions.map(e => ({ label: t(`emirates.${e}`, e), value: e }))} value={input.emirate} onChange={(v: any) => { setInput({ ...input, emirate: v ?? undefined }); setProfileInfoChanged(true); }} searchable /> : <Input value={input.emirate ?? ''} disabled />}
                    />
                  )}
                </dl>

                {/* Contact sub-section */}
                <div className="flex items-center gap-2 mt-6 mb-4">
                  <AppIcon name="Mail" size={16} className="text-primary" />
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('CONTACT')}</h3>
                </div>
                {!editMode ? (
                  <div className="flex flex-col gap-2">
                    {input.email && (
                      <a href={`mailto:${input.email}`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all" aria-label={t('Work Email') + ': ' + input.email}>
                        <AppIcon name="Mail" size={14} className="shrink-0" />
                        <span>{input.email}</span>
                        <span className="text-xs text-muted-foreground">({t('Work Email')})</span>
                      </a>
                    )}
                    {input.personalEmail && (
                      <a href={`mailto:${input.personalEmail}`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all" aria-label={t('Personal Email') + ': ' + input.personalEmail}>
                        <AppIcon name="Mail" size={14} className="shrink-0" />
                        <span>{input.personalEmail}</span>
                        <span className="text-xs text-muted-foreground">({t('Personal Email')})</span>
                      </a>
                    )}
                    {input.phoneNumber && (
                      <a href={`tel:${input.phoneNumber}`} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" aria-label={t('Phone') + ': ' + input.phoneNumber}>
                        <AppIcon name="Phone" size={14} className="shrink-0" />
                        {input.phoneNumber}
                      </a>
                    )}
                    {input.linkedInUrl && isSafeUrl(input.linkedInUrl) && (
                      <a href={input.linkedInUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all" aria-label={t('LinkedIn')}>
                        <AppIcon name="Linkedin" size={14} className="shrink-0" />
                        <span>{input.linkedInUrl}</span>
                      </a>
                    )}
                    {input.address && (
                      <span className="inline-flex items-start gap-1.5 text-sm text-foreground">
                        <AppIcon name="MapPin" size={14} className="shrink-0 mt-0.5" />
                        <span className="whitespace-pre-line">{input.address}</span>
                      </span>
                    )}
                    {!input.email && !input.personalEmail && !input.phoneNumber && !input.linkedInUrl && !input.address && (
                      <span className="text-sm text-muted-foreground/60">--</span>
                    )}
                  </div>
                ) : (
                  <dl>
                    <FieldRow
                      editMode={editMode}
                      label={t('Work Email')}
                      value={input.email}
                      editContent={<Input value={input.email} error={emailError ? emailHelper : undefined} onChange={e => { setInput({ ...input, email: e.target.value }); setProfileInfoChanged(true); validateEmail(e.target.value); }} disabled={!canEditField('email')} />}
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('Personal Email')}
                      value={input.personalEmail}
                      editContent={<Input type="email" placeholder={t('Enter your personal email (optional)')} value={input.personalEmail} error={personalEmailError || undefined} onChange={e => { setInput({ ...input, personalEmail: e.target.value }); setProfileInfoChanged(true); validatePersonalEmail(e.target.value); }} disabled={!canEditField('personalEmail')} />}
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('Phone')}
                      value={input.phoneNumber}
                      editContent={<Input value={input.phoneNumber} onChange={e => { setInput({ ...input, phoneNumber: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('phoneNumber')} />}
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('LinkedIn')}
                      value={input.linkedInUrl}
                      editContent={<Input value={input.linkedInUrl} onChange={e => { setInput({ ...input, linkedInUrl: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('linkedInUrl')} />}
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('address')}
                      value={input.address}
                      editContent={<textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px] resize-y" value={input.address} onChange={e => { setInput({ ...input, address: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('address')} />}
                    />
                  </dl>
                )}
              </CardContent>
            </Card>

            {/* RIGHT: Employment */}
            <Card variant="elevated">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <AppIcon name="Briefcase" size={16} className="text-primary" />
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('EMPLOYMENT')}</h3>
                </div>
                <dl>
                  <FieldRow
                    editMode={editMode}
                    label={t('Role')}
                    value={input.role}
                    editContent={canEditField('role') ? <Select options={roles.map(r => ({ label: r, value: r }))} value={input.role} onChange={(v: any) => { setInput({ ...input, role: v ?? SystemRoles.Clocking }); setProfileInfoChanged(true); }} searchable /> : <Input value={input.role} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Job Title')}
                    value={input.title}
                    editContent={<Input value={input.title} onChange={e => { setInput({ ...input, title: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('title')} />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Hire Date')}
                    value={input.hireDate ? new Date(input.hireDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null}
                    editContent={canEditField('hireDate') ? <CustomDatePicker label="" ariaLabel={t('Hire Date')} value={input.hireDate ?? null} onChange={(d: any) => { setInput({ ...input, hireDate: d ?? undefined }); setProfileInfoChanged(true); }} /> : <Input value={input.hireDate ? new Date(input.hireDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Probation End Date')}
                    value={input.probationEndDate ? new Date(input.probationEndDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null}
                    editContent={canEditField('probationEndDate') ? (
                      <div className="flex flex-col gap-1">
                        <CustomDatePicker label="" ariaLabel={t('Probation End Date')} value={input.probationEndDate ?? null} onChange={(d: any) => { setInput({ ...input, probationEndDate: d ?? undefined }); setProfileInfoChanged(true); }} />
                        {input.hireDate && (() => {
                          const maxDays = probationMaxDaysFor(complianceCountry);
                          if (maxDays === null) return null; // no probation cap for non-UAE/KSA
                          const hireMs = new Date(input.hireDate).getTime();
                          const maxDate = new Date(hireMs + maxDays * 24 * 60 * 60 * 1000);
                          const legalRef = probationLegalReferenceFor(complianceCountry);
                          const exceeds = !!input.probationEndDate
                            && new Date(input.probationEndDate).getTime() > maxDate.getTime();
                          const maxDateLabel = maxDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
                          const lawRef = (
                            <LawReference
                              ariaLabel={t('user.profile.probationRefAria', 'Show legal reference')}
                              citations={[{
                                code: legalRef,
                                description: t('user.profile.probationLegalDesc',
                                  'Probation must not exceed {{days}} days from the hire date.',
                                  { days: maxDays }),
                              }]}
                            />
                          );
                          return exceeds ? (
                            <span
                              data-testid="probation-exceeds-warning"
                              className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center gap-1.5"
                            >
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              <span>
                                {t('user.profile.probationWarning',
                                   'Probation exceeds the legal maximum of {{days}} days (max {{date}}).',
                                   { days: maxDays, date: maxDateLabel })}
                              </span>
                              {lawRef}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                              {t('user.profile.probationHint',
                                 'Max {{date}} ({{days}} days from hire date)',
                                 { date: maxDateLabel, days: maxDays })}
                              {lawRef}
                            </span>
                          );
                        })()}
                      </div>
                    ) : <Input value={input.probationEndDate ? new Date(input.probationEndDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Employment Type')}
                    value={input.employmentType ? (employmentTypeLabels[input.employmentType] ?? input.employmentType) : null}
                    editContent={canEditField('employmentType') ? <Select options={[...employmentTypeOptions].map(o => ({ label: employmentTypeLabels[o] ?? o, value: o }))} value={input.employmentType} onChange={(v: any) => { setInput({ ...input, employmentType: v ?? undefined }); setProfileInfoChanged(true); }} /> : <Input value={input.employmentType ? (employmentTypeLabels[input.employmentType] ?? input.employmentType) : ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Managers')}
                    value={(input.managerIds ?? []).map(mid => allUsers.find(u => u.id === mid)?.fullName).filter(Boolean).join(', ')}
                    editContent={canEditField('managerIds') ? <Select options={managerOptions.map(m => ({ label: m.label, value: m.id }))} value={input.managerIds ?? []} onChange={(ids: any) => { setInput({ ...input, managerIds: ids }); setProfileInfoChanged(true); }} searchable multiple /> : <Input value={(input.managerIds ?? []).map(mid => allUsers.find(u => u.id === mid)?.fullName).filter(Boolean).join(', ')} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('residencyCountry')}
                    value={countries.find(c => c.id === input.countryId)?.name}
                    editContent={canEditField('countryId') ? <Select options={countries.map(c => ({ label: c.name, value: c.id }))} value={input.countryId} onChange={(v: any) => { setInput({ ...input, countryId: v || 0 }); setProfileInfoChanged(true); }} searchable /> : <Input value={countries.find(c => c.id === input.countryId)?.name || ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Time Zone')}
                    value={timeZones.find(z => z.id === input.timeZone)?.displayName || input.timeZone}
                    editContent={canEditField('timeZone') ? <Select options={timeZones.map(z => ({ label: z.displayName, value: z.id }))} value={input.timeZone} onChange={(v: any) => { setInput({ ...input, timeZone: v ?? '' }); setProfileInfoChanged(true); }} searchable /> : <Input value={timeZones.find(z => z.id === input.timeZone)?.displayName || input.timeZone || ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Branch')}
                    value={branches.find(b => b.id === input.branchId)?.name}
                    editContent={canEditField('branchId') ? <Select options={branches.map(b => ({ label: b.name, value: b.id }))} value={input.branchId} onChange={(v: any) => { setInput({ ...input, branchId: v || 0 }); setProfileInfoChanged(true); }} searchable /> : <Input value={branches.find(b => b.id === input.branchId)?.name || ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Department')}
                    value={departments.find(d => d.id === input.departmentId)?.name}
                    editContent={canEditField('departmentId') ? <Select options={departments.map(d => ({ label: d.name, value: d.id }))} value={input.departmentId} onChange={(v: any) => { setInput({ ...input, departmentId: v || 0 }); setProfileInfoChanged(true); }} searchable /> : <Input value={departments.find(d => d.id === input.departmentId)?.name || ''} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Employee Unique ID')}
                    value={input.employeeUniqueId}
                    editContent={<Input value={input.employeeUniqueId} onChange={e => { setInput({ ...input, employeeUniqueId: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('employeeUniqueId')} />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Vehicle ID')}
                    value={input.vehicleId}
                    editContent={<Input value={input.vehicleId} onChange={e => { setInput({ ...input, vehicleId: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('vehicleId')} />}
                  />
                </dl>
              </CardContent>
            </Card>
          </div>}

          {/* Legal & Identity - full width, only shown when data exists or in edit mode */}
          {!loading && !fetchError && (editMode || input.molListNumber || input.fileNumber || input.uidNumber || input.visaDesignation || input.passportNumber || input.passportExpiryDate || input.emiratesIdNumber || input.emiratesIdExpiryDate || input.labourCardNumber || input.labourCardExpiryDate || input.visaIdNumber || input.visaExpiryDate || complianceCountry === 'KSA') && <Card variant="elevated">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <AppIcon name="Shield" size={16} className="text-primary" />
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('legalAndIdentity')}</h3>
              </div>
              {(editMode || input.molListNumber || input.fileNumber || input.uidNumber || input.visaDesignation) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <dl>
                  {(editMode || input.molListNumber) && <FieldRow
                    editMode={editMode}
                    label={t('molListNumber')}
                    value={input.molListNumber}
                    editContent={<Input value={input.molListNumber} onChange={e => { setInput({ ...input, molListNumber: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('molListNumber')} />}
                  />}
                  {(editMode || input.fileNumber) && <FieldRow
                    editMode={editMode}
                    label={t('fileNumber')}
                    value={input.fileNumber}
                    editContent={<Input value={input.fileNumber} onChange={e => { setInput({ ...input, fileNumber: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('fileNumber')} />}
                  />}
                </dl>
                <dl>
                  {(editMode || input.uidNumber) && <FieldRow
                    editMode={editMode}
                    label={t('uidNumber')}
                    value={input.uidNumber}
                    editContent={<Input value={input.uidNumber} onChange={e => { setInput({ ...input, uidNumber: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('uidNumber')} />}
                  />}
                  {(editMode || input.visaDesignation) && <FieldRow
                    editMode={editMode}
                    label={t('visaDesignation')}
                    value={input.visaDesignation}
                    editContent={<Input value={input.visaDesignation} onChange={e => { setInput({ ...input, visaDesignation: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('visaDesignation')} />}
                  />}
                </dl>
              </div>
              )}

              {/* Document-sourced identity fields (read-only, auto-filled from documents) */}
              {(input.passportNumber || input.passportExpiryDate) && (
                <div className="mt-3 rounded-lg bg-muted/30 p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                    <AppIcon name="FileText" size={14} />
                    {t('passport')}
                    <span className="text-xs font-normal normal-case text-muted-foreground/70">{'\u2014'} {t('autoFilledFrom', { docType: t('passport') })}</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm mt-1">
                    {input.passportNumber && (
                      <div><span className="text-muted-foreground">{t('passportNumber')}:</span> <span className="text-foreground">{input.passportNumber}</span></div>
                    )}
                    {input.passportExpiryDate && (
                      <div><span className="text-muted-foreground">{t('passportExpiryDate')}:</span> <span className="text-foreground">{formatDate(new Date(input.passportExpiryDate))}</span></div>
                    )}
                  </div>
                </div>
              )}

              {(input.emiratesIdNumber || input.emiratesIdExpiryDate) && (
                <div className="mt-3 rounded-lg bg-muted/30 p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                    <AppIcon name="FileText" size={14} />
                    {t('emiratesId')}
                    <span className="text-xs font-normal normal-case text-muted-foreground/70">{'\u2014'} {t('autoFilledFrom', { docType: t('emiratesId') })}</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm mt-1">
                    {input.emiratesIdNumber && (
                      <div><span className="text-muted-foreground">{t('emiratesIdNumber')}:</span> <span className="text-foreground">{input.emiratesIdNumber}</span></div>
                    )}
                    {input.emiratesIdExpiryDate && (
                      <div><span className="text-muted-foreground">{t('emiratesIdExpiryDate')}:</span> <span className="text-foreground">{formatDate(new Date(input.emiratesIdExpiryDate))}</span></div>
                    )}
                  </div>
                </div>
              )}

              {(input.labourCardNumber || input.labourCardExpiryDate) && (
                <div className="mt-3 rounded-lg bg-muted/30 p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                    <AppIcon name="FileText" size={14} />
                    {t('labourCard')}
                    <span className="text-xs font-normal normal-case text-muted-foreground/70">{'\u2014'} {t('autoFilledFrom', { docType: t('labourCard') })}</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm mt-1">
                    {input.labourCardNumber && (
                      <div><span className="text-muted-foreground">{t('labourCardNumber')}:</span> <span className="text-foreground">{input.labourCardNumber}</span></div>
                    )}
                    {input.labourCardExpiryDate && (
                      <div><span className="text-muted-foreground">{t('labourCardExpiryDate')}:</span> <span className="text-foreground">{formatDate(new Date(input.labourCardExpiryDate))}</span></div>
                    )}
                  </div>
                </div>
              )}

              {(input.visaIdNumber || input.visaExpiryDate) && (
                <div className="mt-3 rounded-lg bg-muted/30 p-3 space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                    <AppIcon name="FileText" size={14} />
                    {t('visa')}
                    <span className="text-xs font-normal normal-case text-muted-foreground/70">{'\u2014'} {t('autoFilledFrom', { docType: t('visa') })}</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm mt-1">
                    {input.visaIdNumber && (
                      <div><span className="text-muted-foreground">{t('visaIdNumber')}:</span> <span className="text-foreground">{input.visaIdNumber}</span></div>
                    )}
                    {input.visaExpiryDate && (
                      <div><span className="text-muted-foreground">{t('visaExpiryDate')}:</span> <span className="text-foreground">{formatDate(new Date(input.visaExpiryDate))}</span></div>
                    )}
                  </div>
                </div>
              )}

              {complianceCountry === 'KSA' && (
                <div id="ksa-identity" className="mt-3 rounded-lg bg-muted/30 p-3 space-y-2 scroll-mt-24">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                    <AppIcon name="FileText" size={14} />
                    {t('ksaIdentity')}
                  </p>
                  <dl>
                    <FieldRow
                      editMode={editMode}
                      label={t('iqamaNumber')}
                      value={input.iqamaNumber}
                      editContent={
                        <Input
                          aria-label={t('iqamaNumber')}
                          value={input.iqamaNumber ?? ''}
                          maxLength={10}
                          onChange={e => {
                            const v = e.target.value;
                            setInput({ ...input, iqamaNumber: v });
                            validateIqama(v);
                            setProfileInfoChanged(true);
                          }}
                          disabled={!canEditField('iqamaNumber')}
                          error={iqamaError || undefined}
                        />
                      }
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('iqamaExpiryDate')}
                      value={input.iqamaExpiryDate ? formatDate(new Date(input.iqamaExpiryDate)) : ''}
                      editContent={
                        <CustomDatePicker
                          label=""
                          ariaLabel={t('iqamaExpiryDate')}
                          value={input.iqamaExpiryDate ?? null}
                          onChange={(d: any) => {
                            setInput({ ...input, iqamaExpiryDate: d ? formatDateForInput(d) : undefined });
                            setProfileInfoChanged(true);
                          }}
                          disabled={!canEditField('iqamaExpiryDate')}
                        />
                      }
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('saudiNationalIdNumber')}
                      value={input.saudiNationalIdNumber}
                      editContent={
                        <Input
                          aria-label={t('saudiNationalIdNumber')}
                          value={input.saudiNationalIdNumber ?? ''}
                          maxLength={10}
                          onChange={e => {
                            const v = e.target.value;
                            setInput({ ...input, saudiNationalIdNumber: v });
                            validateSaudiNationalId(v);
                            setProfileInfoChanged(true);
                          }}
                          disabled={!canEditField('saudiNationalIdNumber')}
                          error={saudiNationalIdError || undefined}
                        />
                      }
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('qiwaContractNumber')}
                      value={input.qiwaContractNumber}
                      editContent={
                        <Input
                          aria-label={t('qiwaContractNumber')}
                          value={input.qiwaContractNumber ?? ''}
                          onChange={e => {
                            setInput({ ...input, qiwaContractNumber: e.target.value });
                            setProfileInfoChanged(true);
                          }}
                          disabled={!canEditField('qiwaContractNumber')}
                        />
                      }
                    />
                    <FieldRow
                      editMode={editMode}
                      label={t('muqeemIdNumber')}
                      value={input.muqeemIdNumber}
                      editContent={
                        <Input
                          aria-label={t('muqeemIdNumber')}
                          value={input.muqeemIdNumber ?? ''}
                          onChange={e => {
                            setInput({ ...input, muqeemIdNumber: e.target.value });
                            setProfileInfoChanged(true);
                          }}
                          disabled={!canEditField('muqeemIdNumber')}
                        />
                      }
                    />
                    {!input.saudiNationalIdNumber && (
                      <FieldRow
                        editMode={editMode}
                        label={t('profile.personal.visaCategory')}
                        value={input.visaCategory ? t(`profile.personal.visaCategoryOptions.${input.visaCategory}`) : ''}
                        editContent={
                          <select
                            aria-label={t('profile.personal.visaCategory')}
                            value={input.visaCategory ?? ''}
                            onChange={e => {
                              setInput({ ...input, visaCategory: e.target.value || undefined });
                              setProfileInfoChanged(true);
                            }}
                            disabled={!canEditField('visaCategory')}
                            className="w-full rounded border bg-background px-3 py-2 text-sm"
                          >
                            <option value="">—</option>
                            <option value="SeniorExecutive">{t('profile.personal.visaCategoryOptions.SeniorExecutive')}</option>
                            <option value="Professional">{t('profile.personal.visaCategoryOptions.Professional')}</option>
                            <option value="Skilled">{t('profile.personal.visaCategoryOptions.Skilled')}</option>
                            <option value="SemiSkilled">{t('profile.personal.visaCategoryOptions.SemiSkilled')}</option>
                            <option value="Domestic">{t('profile.personal.visaCategoryOptions.Domestic')}</option>
                          </select>
                        }
                      />
                    )}
                  </dl>


                  {input.saudiNationalIdNumber && (
                    <div id="gosi" className="mt-3 border-t border-border/40 pt-3 space-y-2 scroll-mt-24">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                        <AppIcon name="Landmark" size={14} />
                        {t('profile.gosi.title')}
                      </p>
                      <dl>
                        <FieldRow
                          editMode={editMode}
                          label={t('profile.gosi.subscriptionDate')}
                          value={gosiSubscriptionDate ? formatDate(new Date(gosiSubscriptionDate)) : ''}
                          editContent={
                            <CustomDatePicker
                              label=""
                              ariaLabel={t('profile.gosi.subscriptionDate')}
                              value={gosiSubscriptionDate || null}
                              onChange={(d: any) => {
                                const v = d ? formatDateForInput(d) : '';
                                setGosiSubscriptionDate(v);
                                // Auto-default: subscriptions before 2024-07-03 default to legacy scheme.
                                setGosiIsLegacy(!!v && new Date(v) < new Date('2024-07-03'));
                                setGosiSubscriptionError(v ? null : t('profile.gosi.errors.subscriptionRequired'));
                                setGosiDirty(true);
                                setProfileInfoChanged(true);
                              }}
                              disabled={!canEditField('saudiNationalIdNumber')}
                              error={gosiSubscriptionError || undefined}
                              helperText={gosiSubscriptionError || undefined}
                            />
                          }
                        />
                        <FieldRow
                          editMode={editMode}
                          label={t('profile.gosi.legacyScheme')}
                          value={gosiIsLegacy ? t('Yes') : t('No')}
                          editContent={
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                aria-label={t('profile.gosi.legacyScheme')}
                                checked={gosiIsLegacy}
                                onChange={e => {
                                  setGosiIsLegacy(e.target.checked);
                                  setGosiDirty(true);
                                  setProfileInfoChanged(true);
                                }}
                                disabled={!canEditField('saudiNationalIdNumber')}
                                className="h-4 w-4 rounded border border-input bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                              />
                              <span className="text-xs text-muted-foreground">{t('profile.gosi.legacyHint')}</span>
                            </label>
                          }
                        />
                      </dl>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>}

          {/* Financial - full width */}
          {!loading && !fetchError && <Card variant="elevated">
            <CardContent className="p-5">
              <div id="iban" className="flex items-center gap-2 mb-4 scroll-mt-24">
                <AppIcon name="Landmark" size={16} className="text-primary" />
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('FINANCIAL & CURRENCY / BANK')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <dl>
                  <FieldRow
                    editMode={editMode}
                    label={t('Fiscal Year Starting Month')}
                    value={t(monthsArray[(input.fiscalYearStartingMonth ?? 1) - 1])}
                    editContent={canEditField('fiscalYearStartingMonth') ? <Select options={monthsArray.map(m => ({ label: t(m), value: m }))} value={monthsArray[(input.fiscalYearStartingMonth ?? 1) - 1]} onChange={(v: any) => { setInput({ ...input, fiscalYearStartingMonth: monthsArray.indexOf(v ?? 'January') + 1 }); setProfileInfoChanged(true); }} /> : <Input value={t(monthsArray[(input.fiscalYearStartingMonth ?? 1) - 1])} disabled />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('IBAN Number')}
                    value={input.ibanNumber}
                    editContent={<Input value={input.ibanNumber} error={ibanError ? ibanHelper : undefined} onChange={e => { setInput({ ...input, ibanNumber: e.target.value }); setProfileInfoChanged(true); validateIban(e.target.value); }} disabled={!canEditField('ibanNumber')} />}
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('bankAccountNumber')}
                    value={input.bankAccountNumber}
                    editContent={<Input value={input.bankAccountNumber} onChange={e => { setInput({ ...input, bankAccountNumber: e.target.value }); setProfileInfoChanged(true); }} disabled={!canEditField('bankAccountNumber')} />}
                  />
                </dl>
                <dl>
                  <FieldRow
                    editMode={editMode}
                    label={t('Currency Type')}
                    value={
                      !editMode ? (
                        <span className="inline-flex items-center gap-2">
                          <span>{userCurrencyRule}</span>
                          {userCurrency?.currencyCode && (
                            <span className="inline-flex items-center gap-1 bg-muted rounded-full px-2 py-0.5 text-xs font-medium">
                              <ReactCountryFlag countryCode={userCurrency?.country?.iso?.toLowerCase() || userCurrency?.country?.iso3?.toLowerCase() || ''} svg style={{ width: '1em', height: '1em' }} aria-hidden="true" alt="" />
                              {userCurrency.currencyCode}
                            </span>
                          )}
                        </span>
                      ) : null
                    }
                    editContent={
                      <div className="flex flex-col gap-2">
                        <Select options={Object.values(UserCurrency).map(c => ({ label: c, value: c }))} value={userCurrencyRule} onChange={(v: any) => CurrencyRuleChanged(v)} />
                        {(newCurrencyRule === UserCurrency.CustomCurrency || props.userCurrencyRule === UserCurrency.CustomCurrency) && (
                          <Select options={currencies.map(c => ({ label: c.currencyCode, value: c.id, description: c.country?.name }))} value={newCurrency?.id ?? null} onChange={(v: any) => { const selected = currencies.find(c => c.id === v); CurrencyChanged(selected); }} searchable />
                        )}
                      </div>
                    }
                  />
                  <FieldRow
                    editMode={editMode}
                    label={t('Bank Type')}
                    value={
                      !editMode ? (
                        <span className="inline-flex items-center gap-2">
                          <span>{userBankRule}</span>
                          {userBank?.name && <span className="text-xs bg-muted rounded px-1.5 py-0.5">{userBank.name}</span>}
                        </span>
                      ) : null
                    }
                    editContent={
                      <div className="flex flex-col gap-2">
                        <Select options={Object.values(UserBank).map(b => ({ label: b, value: b }))} value={userBankRule} onChange={(v: any) => BankRuleChanged(v)} />
                        {newBankRule === UserBank.CustomBank && (
                          <Select options={banks.map(b => ({ label: b.name, value: b.id }))} value={newBank?.id ?? null} onChange={(v: any) => { const selected = banks.find(b => b.id === v); BankChanged(selected); }} searchable />
                        )}
                      </div>
                    }
                  />
                </dl>
              </div>
            </CardContent>
          </Card>}

          {/* Custom Fields - full width, only shown when HR has defined any */}
          {!loading && !fetchError && customValues.length > 0 && <Card variant="elevated">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <AppIcon name="ClipboardList" size={16} className="text-primary" />
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{t('Custom Fields')}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                {[0, 1].map(col => (
                  <dl key={col}>
                    {customValues.filter((_, i) => i % 2 === col).map(c => (
                      <FieldRow
                        key={c.definitionId}
                        editMode={editMode}
                        label={customFieldLabel(c)}
                        value={c.value}
                        editContent={<Input value={c.value ?? ''} onChange={e => updateCustomValue(c.definitionId, e.target.value)} disabled={!isHr} />}
                      />
                    ))}
                  </dl>
                ))}
              </div>
            </CardContent>
          </Card>}

          {/* Education & Work Experience */}
          {!loading && !fetchError && !hideEducationWorkDocs && (
            <div className="space-y-5">
              <EducationSection userId={(id ?? userId) as string} />
              <WorkExperienceSection userId={(id ?? userId) as string} />
            </div>
          )}

          {/* T&C acceptance — HR-only, only when viewing another employee.
              The endpoint is Policy="Hr"; self-view shows the same info via
              the onboarding gate so there's no value duplicating it here. */}
          {isHr && isViewingAnotherUser && (
            <TermsAcceptanceSection userId={(id ?? userId) as string} />
          )}

          {/* Terminate / Offboarding Dialog */}
          <Dialog open={openTerminate} onOpenChange={(open) => { if (!open && offboardingSubmitting) return; setOpenTerminate(open); }}>
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>{t('Terminate Employee')}</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('Select an offboarding template to run a checklist, or leave it empty to terminate the employee immediately.')}
                </p>

                {offboardingError && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive mb-4">
                    {offboardingError}
                  </div>
                )}

                <div className="space-y-4">
                  <Select
                    label={`${t('Offboarding Template')} (${t('optional')})`}
                    options={[
                      { label: t('No template'), value: '' },
                      ...offboardingTemplates.map(x => ({
                        label: x.name + (x.isDefault ? ` (${t('Default')})` : ''),
                        value: String(x.id)
                      }))
                    ]}
                    value={offboardingFormData.templateId}
                    onChange={(value: any) => setOffboardingFormData(prev => ({ ...prev, templateId: value }))}
                  />

                  {!offboardingFormData.templateId && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
                      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                      <span>{t('No checklist selected. This terminates the employee immediately and calculates end-of-service now.')}</span>
                    </div>
                  )}

                  <CustomDatePicker
                    label={t('Last Working Day')}
                    required
                    value={offboardingFormData.lastWorkingDay || null}
                    onChange={(d: any) => setOffboardingFormData(prev => ({ ...prev, lastWorkingDay: d ? formatDateForInput(d) : '' }))}
                  />
                  <Select
                    label={t('Exit Reason')}
                    required
                    options={[
                      { label: t('Select exit reason...'), value: '' },
                      ...offboardingExitReasons.map(x => ({ label: x.name, value: String(x.id) }))
                    ]}
                    value={offboardingFormData.exitReasonId}
                    onChange={(value: any) => setOffboardingFormData(prev => ({ ...prev, exitReasonId: value }))}
                  />
                  {/* Responsible manager is only meaningful for the managed (templated) offboarding path. */}
                  {!!offboardingFormData.templateId && (
                    <Select
                      label={t('Responsible Manager')}
                      required
                      options={[
                        { label: t('Select manager...'), value: '' },
                        ...offboardingManagers.map(x => ({ label: x.fullName, value: x.id }))
                      ]}
                      value={offboardingFormData.managerId}
                      onChange={(value: any) => setOffboardingFormData(prev => ({ ...prev, managerId: value }))}
                    />
                  )}
                </div>
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={
                    !offboardingFormData.lastWorkingDay ||
                    !offboardingFormData.exitReasonId ||
                    (!!offboardingFormData.templateId && !offboardingFormData.managerId) ||
                    offboardingSubmitting
                  }
                  onClick={offboardingFormData.templateId ? handleStartOffboarding : handleFastTerminate}
                >
                  {offboardingSubmitting
                    ? (offboardingFormData.templateId ? t('Starting...') : t('Terminating...'))
                    : (offboardingFormData.templateId ? t('Start Offboarding') : t('Terminate now'))}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reverse Termination Dialog (A4) — HR correction that returns the employee to active.
              Dismissal via X / backdrop / Esc / Cancel is all funnelled through onOpenChange, so the
              in-flight guard below locks every path together while the reversal is submitting. */}
          <Dialog open={openReverse} onOpenChange={(open) => { if (!open && reverseSubmitting) return; setOpenReverse(open); }}>
            <DialogContent size="md">
              <DialogHeader>
                <DialogTitle>{t('Reverse termination')}</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('This returns the employee to active employment, archives their end-of-service record and any draft settlement, and marks them eligible for rehire. A paid settlement stays untouched unless you override below.')}
                </p>

                {reverseError && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive mb-4">
                    {reverseError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t('Reason for reversal')} <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[76px] resize-y"
                      value={reverseReason}
                      onChange={e => { setReverseReason(e.target.value); if (reverseError) setReverseError(null); }}
                      placeholder={t('Explain why this termination is being reversed. This is recorded in the audit log.')}
                      disabled={reverseSubmitting}
                      aria-label={t('Reason for reversal')}
                    />
                  </div>

                  {reverseHasPaidSettlement && (
                    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${reverseAllowPaid ? 'border-destructive bg-destructive/10' : 'border-border bg-muted/30 hover:bg-muted/50'}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
                        checked={reverseAllowPaid}
                        onChange={e => setReverseAllowPaid(e.target.checked)}
                        disabled={reverseSubmitting}
                      />
                      <span className="text-sm">
                        <span className={`font-medium ${reverseAllowPaid ? 'text-destructive' : 'text-foreground'}`}>
                          {t('This employee has a paid final settlement. Reverse it anyway.')}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {t('The paid settlement will be archived and the reversal recorded in the settlement audit trail.')}
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenReverse(false)} disabled={reverseSubmitting}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="destructive"
                  disabled={!reverseReason.trim() || reverseSubmitting || (reverseHasPaidSettlement && !reverseAllowPaid)}
                  onClick={handleReverseTermination}
                >
                  {reverseSubmitting ? t('Reversing...') : t('Reverse termination')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Toast - unchanged */}
          {toast && (
            <div className={`fixed bottom-4 end-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-destructive'}`}>
              <span>{toast.message}</span>
              <button onClick={() => setToast(null)} className="ms-2 hover:opacity-80" aria-label="Dismiss">
                <AppIcon name="X" size={16} />
              </button>
            </div>
          )}
        </div>
      </UserProfile>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  role: state.user.role,
  userId: state.user.userId,
  imageUrl: state.user.imageUrl,
  userFaceImage: state.user.userFaceImage,
  companyCurrency: state.user.companyCurrency,
  companyCurrencyId: state.user.companyCurrencyId,
  userCurrencyRule: state.user.userCurrencyRule,
  UserCurrencyId: state.user.UserCurrencyId,
  UserCurrency: state.user.UserCurrency,
  company: state.user.company
});

const mapDispatchToProps = (dispatch: any) => ({
  setImageUrl: (imageUrl: string) => dispatch(setImageUrl(imageUrl)),
  setUserFaceImage: (img: string) => dispatch(setUserFaceImage(img)),
  setSelectedUser: (u: UserView | undefined) => dispatch(setSelectedUser(u)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Personal) as any;
